"use client";

import { useAppContext } from "@/context/Context";
import { useState, useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { signIn } from "next-auth/react";
import Loading from "@/components/Common/Loading";
import Image from "next/image";
import Link from "next/link";
import logo from "../../public/images/logo/logo.webp";
import google from "../../public/images/sign-up/google.png";
import logoDark from "../../public/images/light/logo/logo-dark.webp";

// -------- Config email ----------
const EMAIL_PATTERN = "^[^\\s@]+@[^\\s@]+\\.[^\\s@]{2,}$";
const EMAIL_ERROR =
  "Introduce un correo electrónico válido (ej. usuario@gmail.com).";
const PUBLIC_DOMAINS = ["gmail.com", "hotmail.com", "outlook.com", "yahoo.com"];

// ------- Input con autocompletado de dominios (↑/↓/Enter/Tab/Esc) -------
function EmailInput({ name = "email", onInvalid, onInput }) {
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1); // índice activo
  const [kbdNav, setKbdNav] = useState(false); // estamos navegando con teclado

  const updateSuggestions = (v) => {
    const at = v.indexOf("@");
    if (at === -1) {
      setSuggestions([]);
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    const domainFrag = v.slice(at + 1).toLowerCase();
    const list = PUBLIC_DOMAINS.filter((d) => d.startsWith(domainFrag)).slice(
      0,
      6
    );
    const exactIdx = list.findIndex((d) => d === domainFrag);

    setSuggestions(list);

    if (list.length === 0) {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (exactIdx > -1) {
      const local = v.slice(0, at);
      setValue(`${local}@${list[exactIdx]}`);
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    // No hay match exacto: abre y deja seleccionado el primero
    setOpen(true);
    setActiveIndex(0);
  };

  const applyDomain = (dom) => {
    const at = value.indexOf("@");
    const local = value.slice(0, at >= 0 ? at : value.length);
    const next = `${local}@${dom}`;
    setValue(next);
    setOpen(false);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setValue(v);
    setKbdNav(false); // al escribir, asumimos modo texto/mouse
    updateSuggestions(v);
    onInput?.(e);
  };

  const handleFocus = () => {
    if (value.includes("@")) updateSuggestions(value);
  };

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 120); // permite click en la lista
  };

  const handleKeyDown = (e) => {
    // abrir con flechas si hay @
    if (
      !open &&
      value.includes("@") &&
      (e.key === "ArrowDown" || e.key === "ArrowUp")
    ) {
      setKbdNav(true);
      updateSuggestions(value);
      return;
    }
    if (!open || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setKbdNav(true);
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setKbdNav(true);
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const idx = activeIndex > -1 ? activeIndex : 0;
      applyDomain(suggestions[idx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // scroll al activo
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const el = document.getElementById(`email-opt-${activeIndex}`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  // cuando el usuario pasa el mouse por la lista, volvemos a modo mouse
  const onItemMouseEnter = (i) => {
    setKbdNav(false);
    setActiveIndex(i);
  };

  return (
    <div className="input-section relative">
      <div className="icon">
        <i className="fa-sharp fa-regular fa-envelope"></i>
      </div>

      <input
        name={name}
        type="email"
        placeholder="Correo electrónico"
        required
        maxLength={100}
        pattern={EMAIL_PATTERN}
        title={EMAIL_ERROR}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onInvalid={onInvalid}
        onInput={onInput}
        autoCapitalize="none"
        spellCheck={false}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls="email-suggestions"
        aria-activedescendant={
          open && activeIndex > -1 ? `email-opt-${activeIndex}` : undefined
        }
      />

      {open && (
        <div
          className={`email-input-section chat-history-section ${kbdNav ? "kbd-nav" : ""}`}
        >
          <ul
            className="chat-history-list"
            id="email-suggestions"
            role="listbox"
          >
            {suggestions.map((dom, i) => (
              <li
                id={`email-opt-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                key={dom}
                className={`history-box ${i === activeIndex ? "is-active active" : ""}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => onItemMouseEnter(i)}
                onClick={() => applyDomain(dom)}
              >
                {value.includes("@") ? `${value.split("@")[0]}@${dom}` : dom}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ----------------- Página -----------------
export default function Signup() {
  const { status } = useSession();
  const { isLightTheme } = useAppContext();
  const router = useRouter();
  const apiUrl = process.env.NEXT_PUBLIC_API_URL;

  const [serverMessage, setServerMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Tiempo de carga del form (para tiempo mínimo humano)
  const formLoadedAtRef = useRef(Date.now());

  useEffect(() => {
    if (status === "authenticated") router.push("/crear-clase");
  }, [status, router]);

  // Mismo mensaje para type=email y pattern
  const onInvalid = useCallback((e) => {
    const target = e.target;
    const msg = (target.title && target.title.trim()) || EMAIL_ERROR;
    target.setCustomValidity(msg);
  }, []);

  const onInput = useCallback((e) => {
    e.target.setCustomValidity("");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerMessage("");
    setIsSubmitting(true);

    const form = e.currentTarget;
    const name = form.elements.namedItem("name");
    const email = form.elements.namedItem("email");
    const password = form.elements.namedItem("password");
    const confirmPassword = form.elements.namedItem("confirmPassword");

    // Honeypot + elapsedMs (tiempo mínimo)
    const company = (
      form.elements.namedItem("company")?.value || ""
    ).toString();
    const elapsedMs = Date.now() - (formLoadedAtRef.current || Date.now());

    if (password.value !== confirmPassword.value) {
      confirmPassword.setCustomValidity("Las contraseñas no coinciden.");
      form.reportValidity();
      setIsSubmitting(false);
      return;
    }
    confirmPassword.setCustomValidity("");

    if (!form.checkValidity()) {
      form.reportValidity();
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/user/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.value,
          email: email.value, // sin normalización en frontend
          password: password.value,
          company, // honeypot
          elapsedMs, // tiempo desde carga del form
        }),
      });

      const result = await response.json();

      if (response.ok) {
        const PENDING_EMAIL_KEY = `tutoriaplus-app:${process.env.NEXT_PUBLIC_ENV.toLowerCase()}:pendingEmail`;
        sessionStorage.setItem(PENDING_EMAIL_KEY, email.value);

        router.push(
          `/registro/confirmacion?correo=${encodeURIComponent(email.value)}`
        );
      } else {
        setIsSubmitting(false);
        setServerMessage(
          result.detail || result.message || "Error en el registro."
        );
      }
    } catch {
      setIsSubmitting(false);
      setServerMessage(
        "Ocurrió un error. Por favor, intenta nuevamente más tarde."
      );
    }
  };

  if (status === "loading" || status === "authenticated") return <Loading />;

  return (
    <main className="page-wrapper">
      <div className="signup-area">
        <div className="wrapper">
          <div className="row">
            <div className="col-lg-12 bg-color-blackest left-wrapper">
              <div className="sign-up-box">
                <div className="signup-box-top">
                  <Image
                    src={isLightTheme ? logo : logoDark}
                    width={193}
                    height={50}
                    alt="sign-up logo"
                  />
                </div>

                <div className="signup-box-bottom">
                  <div className="signup-box-content">
                    <h3 className="title mb--30">Crear cuenta</h3>

                    <form
                      onSubmit={handleSubmit}
                      noValidate
                      className="sign-form-section"
                    >
                      {serverMessage && (
                        <div className="col-12">
                          <div className="form-group">
                            <span className="alert-message d-block">
                              {serverMessage}
                            </span>
                          </div>
                        </div>
                      )}

                      <input
                        type="text"
                        name="company"
                        defaultValue=""
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        className="honeypot"
                      />

                      {/* Nombre */}
                      <div className="input-section">
                        <div className="icon">
                          <i className="fa-sharp fa-regular feather-user"></i>
                        </div>
                        <input
                          name="name"
                          type="text"
                          placeholder="Nombre"
                          required
                          minLength={3}
                          maxLength={100}
                          title="El nombre debe tener al menos 3 caracteres."
                          onInvalid={onInvalid}
                          onInput={onInput}
                        />
                      </div>

                      {/* Email con autocompletado de dominios */}
                      <EmailInput onInvalid={onInvalid} onInput={onInput} />

                      {/* Password */}
                      <div className="input-section">
                        <div className="icon">
                          <i className="fa-sharp fa-regular fa-lock"></i>
                        </div>
                        <input
                          name="password"
                          type="password"
                          placeholder="Contraseña"
                          required
                          minLength={6}
                          maxLength={100}
                          pattern="(?=.*[A-Za-z])(?=.*[0-9]).{6,}"
                          title="La contraseña debe tener al menos 6 caracteres e incluir al menos una letra y un número."
                          onInvalid={onInvalid}
                          onInput={onInput}
                        />
                      </div>

                      {/* Confirm Password */}
                      <div className="input-section">
                        <div className="icon">
                          <i className="fa-sharp fa-regular fa-lock"></i>
                        </div>
                        <input
                          name="confirmPassword"
                          type="password"
                          placeholder="Reingresa tu contraseña"
                          required
                          maxLength={100}
                          title="Debes confirmar tu contraseña."
                          onInvalid={onInvalid}
                          onInput={onInput}
                        />
                      </div>

                      <button
                        type="submit"
                        className="btn-default"
                        disabled={isSubmitting}
                      >
                        Registrarte gratis
                        {isSubmitting && (
                          <i className="feather-loader spinner-icon"></i>
                        )}
                      </button>
                    </form>

                    <div className="signup-box-footer">
                      <div className="bottom-text">
                        ¿Ya tienes una cuenta?
                        <Link
                          className="btn-read-more ml--5"
                          href="/iniciar-sesion"
                        >
                          <span>Iniciar sesión</span>
                        </Link>
                      </div>
                    </div>

                    <div className="text-social-area">
                      <hr />
                      <span>o</span>
                      <hr />
                    </div>
                    <div className="social-btn-grp">
                      <button
                        type="button"
                        className="btn-default btn-border"
                        onClick={() =>
                          signIn("google", { callbackUrl: "/acceso-google" })
                        }
                      >
                        <span className="icon-left">
                          <Image
                            src={google}
                            width={18}
                            height={18}
                            alt="Google Icon"
                          />
                        </span>
                        Iniciar sesión con Google
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Link className="close-button" href="/">
          <i className="fa-sharp fa-regular fa-x"></i>
        </Link>
      </div>
    </main>
  );
}
